"use client"

import { useContext } from "react"
import {
  Box,
  Avatar,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Divider,
  Paper,
  Chip,
  alpha,
  useTheme,
  Alert,
  Fade,
  Skeleton,
  Stack,
  Button,
  Grid,
  Link,
} from "@mui/material"
import {
  Link as LinkIcon,
  Description as DescriptionIcon,
  CalendarMonth as CalendarIcon,
  Image as ImageIcon,
  NewReleases as NewsIcon,
  LocalOffer as PromotionIcon,
  ToggleOn as ActiveIcon,
  ToggleOff as InactiveIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material"
import axios from "axios"
import { useQuery } from "@tanstack/react-query"
import { AppContext } from "../../contexts/AppContext"

function PromotionInfo({ idPartner, idPromotion, onBack }) {
  const { state } = useContext(AppContext)
  const theme = useTheme()

  const { data, isLoading, error } = useQuery({
    queryKey: ["promotion", idPromotion],
    queryFn: async () => {
      if (!idPromotion) return null
      const response = await axios.get(`/api/v2/partner/${idPartner}/announcements/${idPromotion}`, {
        headers: { Authorization: state.token ? `Bearer ${state.token}` : "" },
      })
      return response.data
    },
    enabled: !!idPromotion,
  })

  if (isLoading) {
    return (
      <Card elevation={3} sx={{ maxWidth: 600, mx: "auto", mt: 2, borderRadius: 2 }}>
        <CardHeader
          title={<Skeleton width="60%" />}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        />
        <CardContent>
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={56} />
            <Skeleton variant="rectangular" height={56} />
            <Skeleton variant="rectangular" height={56} />
            <Box display="flex" gap={2}>
              <Skeleton variant="rectangular" height={56} width="50%" />
              <Skeleton variant="rectangular" height={56} width="50%" />
            </Box>
            <Skeleton variant="rectangular" height={200} />
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: "flex-end", p: 2 }}>
          <Skeleton variant="rectangular" width={100} height={36} />
        </CardActions>
      </Card>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ maxWidth: 600, mx: "auto", mt: 2 }}>
        Error loading promotion data. Please try again later.
      </Alert>
    )
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ maxWidth: 600, mx: "auto", mt: 2 }}>
        No promotion data found.
      </Alert>
    )
  }

  const {
    label = "promotion",
    url = null,
    text = "",
    start_date = "",
    end_date = "",
    image = "",
    isActive = true,
    hasMedia = [],
  } = data

  const formattedStartDate = start_date
    ? new Date(start_date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Not specified"

  const formattedEndDate = end_date
    ? new Date(end_date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Not specified"

  const mediaId = hasMedia && hasMedia.length > 0 ? hasMedia[0].media_id : null
  const hasImageToShow = image || mediaId

  return (
    <Fade in={true} timeout={300}>
      <Card elevation={3} sx={{ maxWidth: 600, mx: "auto", mt: 2, borderRadius: 2, overflow: "visible" }}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {label === "news" ? (
                <NewsIcon color="info" fontSize="large" />
              ) : (
                <PromotionIcon color="secondary" fontSize="large" />
              )}
              <Typography variant="h5" fontWeight="medium">
                {label === "news" ? "News Details" : "Promotion Details"}
              </Typography>
            </Box>
          }
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: "1px solid",
            borderColor: "divider",
            p: 3,
          }}
          action={
            <Chip
              icon={isActive ? <ActiveIcon /> : <InactiveIcon />}
              label={isActive ? "Active" : "Inactive"}
              color={isActive ? "success" : "error"}
              variant="filled"
              sx={{ fontWeight: "medium", px: 1 }}
            />
          }
        />

        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Description Section */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
                  <DescriptionIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Description
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ pl: 4 }}>
                  {text || "No description provided."}
                </Typography>
              </Paper>
            </Grid>

            {/* URL Section - Only show if URL exists */}
            {url && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
                    <LinkIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight="medium">
                      URL
                    </Typography>
                  </Box>
                  <Link
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ pl: 4, display: "block", wordBreak: "break-all" }}
                  >
                    {url}
                  </Link>
                </Paper>
              </Grid>
            )}

            {/* Dates Section */}
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  height: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <CalendarIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Start Date
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ pl: 4 }}>
                  {formattedStartDate}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  height: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <CalendarIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    End Date
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ pl: 4 }}>
                  {formattedEndDate}
                </Typography>
              </Paper>
            </Grid>

            {/* Image Section */}
            {hasImageToShow && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Chip label="Promotion Image" variant="outlined" icon={<ImageIcon />} />
                </Divider>
                <Paper
                  elevation={2}
                  sx={{
                    width: "100%",
                    height: 300,
                    overflow: "hidden",
                    borderRadius: 2,
                    mt: 2,
                  }}
                >
                  <Avatar
                    src={image || `/api/media/${mediaId}`}
                    variant="square"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Paper>
              </Grid>
            )}
          </Grid>
        </CardContent>

        <CardActions sx={{ justifyContent: "flex-start", p: 3, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{ borderRadius: 1.5 }}
          >
            Back
          </Button>
        </CardActions>
      </Card>
    </Fade>
  )
}

export default PromotionInfo

